/**
 * Inventory filters and search (T078, spec.md User Story 4, FR-019 to FR-022). All in memory: a
 * cellar of 1,000 wines filters well under a second (SC-005, T079).
 *
 * - Type de vin, Domaine: one or more values each (owner, 2026-10-15; one value before), the
 *   Domaine compared after normalisation; a wine matches any of them.
 * - Drink-by years: one or more "à boire avant" years; a wine matches any of them (owner,
 *   2026-10-15, replacing the panel's due/soon/past choice).
 * - Année, Pays: one or more values; a wine matches any of them (owner, 2026-10-01).
 * - Région: one or more regions, "other" or REGION_NONE (wines saved before regions); a wine
 *   matches any of them (owner, 2026-10-03).
 * - Cépage: one or more grape varieties, each variety of a blend on its own (owner, 2026-09-29).
 * - Rating: one or more values (1 to 5 in half points) and/or "not yet rated", matched against
 *   each wine's latest rating (FR-019).
 * - Text: a part of any identity field or tasting note, ignoring case and accents (FR-020).
 * - Wines with no bottles left are hidden unless `includeEmpty` (FR-022).
 * - `due`: only in-stock wines due soon or past their window (`any`, User Story 6, FR-037), or
 *   only one of the two (`soon`, `past`: the home page's cards, owner 2026-10-01). Set from the
 *   address only, no longer offered in the panel.
 * Everything combines with AND.
 */
import { wineStock, type TypeDeVin, type Wine } from './cellar';
import { normaliseIdentityField } from './identity';
import { latestRatedTasting } from './rating';
import { drinkByState, todayInParis, type CalendarDay } from './drink-by';
import { REGION_OTHER } from './regions';

/** The Région filter's value for wines saved before regions existed (shown "À compléter"). */
export const REGION_NONE = 'none';

/** Wines to drink: due soon or past (`any`), or only one of the two. */
export type DueFilter = 'any' | 'soon' | 'past';

/** A selected rating value, or wines that have none yet. */
export type RatingFilterValue = number | 'unrated';

export interface InventoryFilters {
  /** Wine types; a wine matches if it is any of them (owner, 2026-10-15). */
  typesDeVin: TypeDeVin[];
  /** Estates; a wine matches if it is any of them, after normalisation (owner, 2026-10-15). */
  domaines: string[];
  /** "À boire avant" years; a wine matches if it is due in any of them (owner, 2026-10-15). */
  drinkUntilYears: number[];
  /** Vintages; a wine matches if it is any of them (owner, 2026-10-01). */
  annees: number[];
  /** ISO country codes; a wine matches if it is from any of them. */
  pays: string[];
  /** Regions (domain/regions.ts names, "other" or REGION_NONE); a wine matches any of them. */
  regions: string[];
  /** Grape varieties; a wine matches if it contains any of them (owner, 2026-09-29). */
  cepages: string[];
  ratings: RatingFilterValue[];
  text: string;
  includeEmpty: boolean;
  /** Only in-stock wines due soon and/or past their window (FR-037; home cards, 2026-10-01). */
  due?: DueFilter;
}

export const NO_FILTERS: InventoryFilters = {
  typesDeVin: [],
  domaines: [],
  drinkUntilYears: [],
  annees: [],
  pays: [],
  regions: [],
  cepages: [],
  ratings: [],
  text: '',
  includeEmpty: false,
};

/**
 * The grape varieties in a Cépage entry, a blend split on commas, slashes, semicolons, "&", "+"
 * and " et " ("Grenache, Mourvèdre, Syrah" → three). Hyphenated and multi-word names stay whole
 * ("Cabernet-Sauvignon", "Chenin blanc").
 */
export function grapeVarieties(cepage: string): string[] {
  return cepage
    .split(/\s*(?:[,;/&+]|\bet\b)\s*/i)
    .map((variety) => variety.trim())
    .filter((variety) => variety !== '');
}

/** Lower case, no accents, single spaces: the same rule as wine identity (FR-003). */
const normalise = (value: string) => normaliseIdentityField(value);

/** Whether anything narrows the list (an empty search does not). */
export function hasActiveFilters(filters: InventoryFilters): boolean {
  return (
    filters.typesDeVin.length > 0 ||
    filters.domaines.length > 0 ||
    filters.drinkUntilYears.length > 0 ||
    filters.annees.length > 0 ||
    filters.pays.length > 0 ||
    filters.regions.length > 0 ||
    filters.cepages.length > 0 ||
    filters.ratings.length > 0 ||
    normalise(filters.text) !== '' ||
    filters.includeEmpty ||
    filters.due !== undefined
  );
}

/** Everything the text search looks into for one wine, normalised once. */
function searchableText(wine: Wine): string {
  return normalise(
    [
      wine.domaine,
      wine.cepage,
      wine.parcelle ?? '',
      String(wine.annee),
      ...wine.tastings.map((tasting) => tasting.notes ?? ''),
    ].join(' \n '),
  );
}

export function filterWines(
  wines: readonly Wine[],
  filters: InventoryFilters,
  today: CalendarDay = todayInParis(),
): Wine[] {
  const domaines = new Set(filters.domaines.map(normalise));
  const cepages = new Set(filters.cepages.map(normalise));
  const text = normalise(filters.text);
  const wantUnrated = filters.ratings.includes('unrated');
  const wantRatings = new Set(filters.ratings.filter((r): r is number => r !== 'unrated'));

  return wines.filter((wine) => {
    if (!filters.includeEmpty && wineStock(wine) === 0) return false;
    if (filters.typesDeVin.length > 0 && !filters.typesDeVin.includes(wine.typeDeVin)) {
      return false;
    }
    if (filters.pays.length > 0 && !filters.pays.includes(wine.pays)) return false;
    if (filters.regions.length > 0 && !filters.regions.includes(wine.region ?? REGION_NONE)) {
      return false;
    }
    if (filters.annees.length > 0 && !filters.annees.includes(wine.annee)) return false;
    if (domaines.size > 0 && !domaines.has(normalise(wine.domaine))) return false;
    if (
      filters.drinkUntilYears.length > 0 &&
      (wine.drinkUntilYear === undefined || !filters.drinkUntilYears.includes(wine.drinkUntilYear))
    ) {
      return false;
    }
    if (cepages.size > 0 && !grapeVarieties(wine.cepage).some((v) => cepages.has(normalise(v)))) {
      return false;
    }
    if (filters.ratings.length > 0) {
      const latest = latestRatedTasting(wine.tastings)?.rating;
      const matches = latest === undefined ? wantUnrated : wantRatings.has(latest);
      if (!matches) return false;
    }
    if (text !== '' && !searchableText(wine).includes(text)) return false;
    if (filters.due !== undefined) {
      const badge = drinkByState(wine, today).badge;
      if (badge === undefined || (filters.due !== 'any' && badge !== filters.due)) return false;
    }
    return true;
  });
}

export interface FilterOptions {
  domaines: string[];
  cepages: string[];
  /** Newest vintage first. */
  annees: number[];
  pays: string[];
  /** The "à boire avant" years of the cellar's wines, soonest first (owner, 2026-10-15). */
  drinkUntilYears: number[];
}

/** First spelling of each value, one per normalised form, in alphabetical order. */
function distinct(values: string[]): string[] {
  const byKey = new Map<string, string>();
  for (const value of values) {
    const key = normalise(value);
    if (key !== '' && !byKey.has(key)) byKey.set(key, value.trim());
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
}

/** The values the filter lists offer, from every wine of the cellar (in stock or not). */
export function filterOptions(wines: readonly Wine[]): FilterOptions {
  return {
    domaines: distinct(wines.map((w) => w.domaine)),
    cepages: distinct(wines.flatMap((w) => grapeVarieties(w.cepage))),
    annees: [...new Set(wines.map((w) => w.annee))].sort((a, b) => b - a),
    pays: [...new Set(wines.map((w) => w.pays))].sort(),
    drinkUntilYears: [
      ...new Set(wines.flatMap((w) => (w.drinkUntilYear !== undefined ? [w.drinkUntilYear] : []))),
    ].sort((a, b) => a - b),
  };
}

/**
 * The regions the Région filter offers (owner, 2026-10-03): those of the cellar's wines from the
 * ticked countries (every country when none is ticked), A to Z, then "other", then REGION_NONE.
 */
export function regionChoices(wines: readonly Wine[], pays: readonly string[]): string[] {
  const found = new Set(
    wines
      .filter((wine) => pays.length === 0 || pays.includes(wine.pays))
      .map((wine) => wine.region ?? REGION_NONE),
  );
  const named = [...found]
    .filter((region) => region !== REGION_OTHER && region !== REGION_NONE)
    .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  return [
    ...named,
    ...(found.has(REGION_OTHER) ? [REGION_OTHER] : []),
    ...(found.has(REGION_NONE) ? [REGION_NONE] : []),
  ];
}
